import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import { HasShellContext, withIsInShell } from "@insite/client-framework/Components/IsInShell";
import Zone from "@insite/client-framework/Components/Zone";
import { getSession, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import dismissRfqUpdates from "@insite/client-framework/Store/Context/Handlers/DismissRfqUpdates";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getLocation, getPageStateByPath } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import PageModule from "@insite/client-framework/Types/PageModule";
import PageProps from "@insite/client-framework/Types/PageProps";
import SkipNav, { SkipNavStyles } from "@insite/content-library/Components/SkipNav";
import Link from "@insite/mobius/Link";
import Page, { PageProps as ContainerProps } from "@insite/mobius/Page";
import { HasToasterContext, withToaster } from "@insite/mobius/Toast/ToasterContext";
import Typography from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import MarketoCustomModal from "@stoneage/App/Components/MarketoFormModal/MarketoCustomModal";
import { CustomAppState } from "@stoneage/App/Store/Reducers";
import { screen } from "@stoneage/XDL/Helpers";
import debounce from "lodash/debounce";
import React, { FC, useEffect, useState } from "react";
import { connect, ResolveThunks, useDispatch } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: CustomAppState) => {
    const pageType = getCurrentPage(state).type;
    const location = getLocation(state);

    return {
        isSticky: state.xHeader.isSticky,
        siteName: state.context.website.name,
        session: getSession(state),
        myAccountPageUrl: getPageLinkByPageType(state, "MyAccountPage")?.url,
        isHomePage: pageType === "HomePage", // HomePage
        isHeaderPage: pageType === "Header", // HeaderPage
        toggleHeaderColor: state.xHeader.toggleHeaderColor,
        searchInputFocus: state.xHeader.searchInputFocus,
        customSettings: getSettingsCollection(state).customSettings as any,
        location,
    };
};

const mapDispatchToProps = {
    dismissRfqUpdates,
};

const HeaderSize: any = {
    default: 94,
    sticky: 94,
    mobile: 88,
    top: 0,
};

type Props = PageProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasToasterContext &
    HasHistory &
    HasShellContext;

export interface HeaderStyles {
    header?: ContainerProps;
    skipToContent?: SkipNavStyles;
    websiteName?: InjectableCss;
    headerContentWrapper?: InjectableCss;
    logoLinksSearchWrapper?: InjectableCss;
    secondaryNavigationWrapper?: InjectableCss;
    secondaryNavigationPage?: ContainerProps;
    logoLinksSearchPage?: ContainerProps;
    placeholder?: InjectableCss;
}

const headerHoverStyles = `
background-color: #f3f3f3;
border-bottom: 1px solid #dfdfdf;
@media print {
    background-color: transparent;
    border-bottom: initial;
}
`;

export const headerStyles: HeaderStyles = {
    header: {
        as: "header",
        fullWidth: [true, true, true, true, true],
        padding: 0,
        css: css`
            position: relative;
            z-index: 1286;

            @media print {
                display: block !important;
            }
            &:hover,
            &:focus {
                .secondary-navigation-wrapper,
                .main-navigation-wrapper {
                    ${headerHoverStyles}
                }
            }

            .secondary-navigation-wrapper,
            .main-navigation-wrapper {
                ${({ isSticky, isHomePage, toggleHeaderColor }: any) => {
                    if (isSticky || !isHomePage || toggleHeaderColor) {
                        return `
                        ${headerHoverStyles}
                            `;
                    }
                }}

                ${screen("lte", "md")`
                    ${headerHoverStyles}
                `}
            }
        `,
    },
    headerContentWrapper: {
        css: css`
            position: Fixed;
            top: 0;
            left: 0;
            right: 0;
            @media print {
                position: static;
            }
            ${({ isInShell, isEditMode, isHeaderPage }: any) => {
                if (isInShell && !isHeaderPage) {
                    return `
                        position: relative;
                        background-color: rgba(0, 0, 0, 0.33);
                    `;
                }
                if (isInShell && isHeaderPage) {
                    return `
                        background-color: rgba(0, 0, 0, 0.33);
                        position: relative;
                        > div {
                            height: auto;
                        }
                    `;
                }
            }}
        `,
    },
    secondaryNavigationWrapper: {
        css: css`
            display: flex;
            align-items: center;
            width: 100%;
            background-color: rgba(0, 0, 0, 0.33);
            height: 37px;
            padding: 8px 0;
            @media print {
                display: none;
            }
        `,
    },
    logoLinksSearchWrapper: {
        css: css`
            display: flex;
            align-items: center;
            width: 100%;
            background-color: transparent;
            height: 60px;
            padding: 3px 0;

            ${screen("lte", "md")`
                height: 51px;
            `}
        `,
    },
    websiteName: {
        css: css`
            @media print {
                display: block;
            }
            font-size: 12px;
            padding-left: 15px;
            display: none;
        `,
    },
    placeholder: {
        css: css`
            ${({ isHomePage }: any) =>
                !isHomePage &&
                `
                position: relative;
                z-index: -1;
                height: ${HeaderSize.default}px;
            `}

            ${screen("lte", "md")`
                background-color: #F3F3F3;
                height: ${HeaderSize.mobile}px;
                position: relative;
                z-index: -1;
            `}
                @media print {
                display: none;
            }
        `,
    },
    logoLinksSearchPage: {
        css: css`
            display: flex;
            align-items: center;
            .header_links-search {
                @media print {
                    display: none;
                }
            }
        `,
    },
    secondaryNavigationPage: {
        css: css`
            display: flex;
            align-items: center;
        `,
    },
};

const styles = headerStyles;
const StyledWrapperX = getStyledWrapper("div") as any; // typeof StyledWrapper & { sticky: boolean };

const Header = ({
    id,
    siteName,
    session,
    toaster,
    history,
    myAccountPageUrl,
    dismissRfqUpdates,
    isSticky,
    shellContext,
    isHomePage,
    isHeaderPage,
    toggleHeaderColor,
    searchInputFocus,
    customSettings,
    location,
}: Props) => {
    const dispatch = useDispatch();

    const viewMyDashboardClickHandler = () => {
        if (myAccountPageUrl) {
            history.push(myAccountPageUrl);
        }
    };

    useEffect(() => {
        if (localStorage) {
            localStorage.setItem("cloudinaryMediaRootPath", customSettings.cloudinaryMediaRootPath);
            localStorage.setItem("productSmallDefaultImagePath", customSettings.productSmallDefaultImagePath);
            localStorage.setItem("productMediumDefaultImagePath", customSettings.productMediumDefaultImagePath);
            localStorage.setItem("productLargeDefaultImagePath", customSettings.productLargeDefaultImagePath);
            localStorage.setItem("cloudinaryGlobalNotFoundImagePath", customSettings.cloudinaryGlobalNotFoundImagePath);
        }
    }, [customSettings]);

    useEffect(() => {
        if (session.hasRfqUpdates) {
            toaster.addToast({
                body: (
                    <>
                        <Typography>{translate("You have new quote information.")}</Typography>
                        <Link onClick={viewMyDashboardClickHandler}>{translate("View My Dashboard")}</Link>
                    </>
                ),
                messageType: "info",
                timeoutLength: 1000 * 60 * 60 * 24 * 365,
                onClose: () => {
                    dismissRfqUpdates();
                },
            });
        }
    }, []);

    // SAINC-1192: Support Desk - Cross-page anchor linking
    useEffect(() => {
        if (location?.hash?.length) {
            setTimeout(() => {
                const id = location.hash!.replace("#", "");

                const element = document.getElementById(id);
                if (!element) {
                    return;
                }

                element.scrollIntoView();
            }, 200);
        }
    }, [location.hash]);

    const [isHeaderOut, setHeaderOut] = useState(false);
    const [headerElement, setHeaderElement] = useState(undefined);

    const handleSticky = debounce(() => {
        if (headerElement) {
            const headerHeight = (headerElement as any).getBoundingClientRect().height;
            const top = window.scrollY;

            const newValue = top > headerHeight || window.innerWidth < 1025;
            if (newValue !== isSticky) {
                dispatch({ type: "xHeader/isSticky", isSticky: newValue });
            }

            const tempHeaderOut = top > headerHeight;
            if (tempHeaderOut !== isHeaderOut) {
                setHeaderOut(tempHeaderOut);
            }
        }
    }, 0);

    useEffect(() => {
        const headerElement = document.getElementById("header");
        setHeaderElement(headerElement as any);
        document.addEventListener("scroll", handleSticky, { passive: true });
        window.addEventListener("resize", handleSticky);
        handleSticky();
        return () => {
            document.removeEventListener("scroll", handleSticky);
            window.removeEventListener("resize", handleSticky);
        };
    });

    const afterHeader = React.createRef<HTMLSpanElement>();
    return (
        <>
            <Page
                {...styles.header}
                id="header"
                {...({ isSticky } as any)}
                {...({ isHomePage } as any)}
                {...({ isHeaderPage } as any)}
                {...({ toggleHeaderColor } as any)}
                onMouseOver={() => {
                    if (toggleHeaderColor !== true) {
                        dispatch({ type: "xHeader/doToggleHeaderColor", open: true });
                    }
                }}
                onMouseLeave={() => {
                    if (toggleHeaderColor !== false && searchInputFocus === false) {
                        dispatch({ type: "xHeader/doToggleHeaderColor", open: false });
                    }
                }}
                onFocus={() => {
                    if (toggleHeaderColor !== true) {
                        dispatch({ type: "xHeader/doToggleHeaderColor", open: true });
                    }
                }}
            >
                <SkipNav
                    extendedStyles={styles.skipToContent}
                    text={translate("Skip to main content")}
                    destination={afterHeader}
                />
                <StyledWrapperX
                    {...styles.headerContentWrapper}
                    isHeaderPage={isHeaderPage}
                    isInShell={shellContext.isInShell}
                    isEditMode={shellContext.isEditing}
                    className="header_content-wrapper"
                >
                    <StyledWrapper
                        {...styles.secondaryNavigationWrapper}
                        {...({ isHomePage } as any)}
                        {...({ isHeaderPage } as any)}
                        className="secondary-navigation-wrapper"
                    >
                        <Page {...styles.secondaryNavigationPage}>
                            <Zone contentId={id} zoneName="SecondaryNavigation" />
                        </Page>
                    </StyledWrapper>
                    <StyledWrapper
                        {...styles.logoLinksSearchWrapper}
                        {...({ isHomePage } as any)}
                        {...({ isHeaderPage } as any)}
                        className="main-navigation-wrapper"
                    >
                        <Page {...styles.logoLinksSearchPage}>
                            <Zone contentId={id} zoneName="LogoLinksSearch" />
                        </Page>
                    </StyledWrapper>
                </StyledWrapperX>
                <StyledWrapperX
                    className="placeholder"
                    {...styles.placeholder}
                    {...({ isHomePage } as any)}
                ></StyledWrapperX>

                <span ref={afterHeader} tabIndex={-1} />
            </Page>
            <MarketoCustomModal widgetId={id} />
        </>
    );
};

const pageModule: PageModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withIsInShell(withToaster(withHistory(Header)))),
    definition: {
        hasEditableTitle: false,
        hasEditableUrlSegment: false,
        pageType: "System",
    },
};

export const HeaderContext = "HeaderContext";

export default pageModule;
